/** @jsx jsx */
import React, {
	type PropsWithChildren,
	useCallback,
	useEffect,
	useRef,
	useState,
	type MouseEvent,
	type ReactNode,
} from 'react';
import { styled, css, jsx } from '@compiled/react';
import throttle from 'lodash/throttle';
import { Box, Flex, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { useProject } from '@atlassian/jira-business-entity-project-hook/src/index.tsx';
import { useFieldDetails } from '@atlassian/jira-business-inline-field-config-sidebar/src/controllers/field-details/index.tsx';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { COLUMN_DRAGGABLE_HELPER_CLASS } from '../../../constants.tsx';
import type { DraggableStatus } from '../../../controllers/drag-and-drop/types.tsx';
import { useFullHeightColumnDropTarget } from '../../../controllers/drag-and-drop/use-full-height-column-drop-target/index.tsx';
import { useReorderColumns } from '../../../controllers/drag-and-drop/use-reorder-columns/index.tsx';
import { useInlineCreateActions } from '../../../controllers/inline-create-context/index.tsx';
import { FIELD } from '../../../controllers/inline-create-context/types.tsx';
import { useInlineFieldCreateControls } from '../../../controllers/inline-field-create-controls/index.tsx';
import ActionsDropdownMenu from './field-actions/actions-dropdown-menu/index.tsx';
import SortingIndicator from './field-actions/sorting-indicator/index.tsx';
import messages from './messages.tsx';

const BOUNDARY = 70;
const MOUSE_MOVE_THROTTLE = 16;
export const COLUMN_HEADER_TEST_ID =
	'business-list.ui.list-view.column-header.column-header-container';
const COLUMN_HEADER_DRAG_HANDLE_TEST_ID =
	'business-list.ui.list-view.column-header.column-header-container.drag-handle';

type Props = {
	icon?: ReactNode;
	label: string;
	canSortField?: boolean;
	canHideField?: boolean;
	canEditField: boolean;
	fieldId: string;
	fieldType?: string;
	disableDrag?: boolean;
	index: number;
};

const VisualRefreshIconWrapper = ({ children }: { children: ReactNode }) => (
	<Flex justifyContent="center" alignItems="center" xcss={iconWrapperStyle}>
		{children}
	</Flex>
);

export const ColumnHeader = ({
	icon,
	label,
	canSortField = true,
	canHideField = false,
	canEditField = false,
	fieldId,
	fieldType = '',
	disableDrag: isDragDisabled,
	index,
}: Props) => {
	const [isHovering, setIsHovering] = useState(false);
	const { formatMessage } = useIntl();
	const project = useProject();
	const projectId = String(project.id);

	const ref = useRef<HTMLDivElement>(null);

	const { status: dragStatus } = useReorderColumns({
		ref,
		id: fieldId,
		index,
		isDragDisabled,
	});
	const { dropTargetJSX } = useFullHeightColumnDropTarget({
		id: fieldId,
		index,
	});
	const [{ isSelected }] = useInlineFieldCreateControls();
	const { clearIndex, clearIndexAfterDelay, setIndex } = useInlineCreateActions();
	const { getFieldDetails, fieldDetails, called } = useFieldDetails(projectId, false);

	useEffect(() => {
		if (canEditField && isHovering && !fieldDetails && !called) {
			getFieldDetails(fieldId);
		}
	}, [called, canEditField, fieldDetails, fieldId, getFieldDetails, isHovering]);

	// @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ updated: MessageDescriptorV2; created: MessageDescriptorV2; priority: MessageDescriptorV2; duedate: MessageDescriptorV2; ... 5 more ...; issuetype: MessageDescriptorV2; }'. | TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ updated: MessageDescriptorV2; created: MessageDescriptorV2; priority: MessageDescriptorV2; duedate: MessageDescriptorV2; ... 5 more ...; issuetype: MessageDescriptorV2; }'.
	const customLabel = messages[fieldId] ? formatMessage(messages[fieldId]) : label;

	const showActions = canSortField || canHideField || canEditField;
	const editActionIsVisible = canEditField && !!fieldDetails?.operations.editable;

	const columnActions = showActions ? (
		<ActionsWrapper data-testid="business-list.ui.list-view.column-header.actions-wrapper">
			{canSortField && (
				<SortingIndicator columnName={fieldId} isVisible={isHovering} fieldType={fieldType} />
			)}
			<div />
			<ActionsDropdownMenu
				fieldId={fieldId}
				isVisible={isHovering}
				fieldType={fieldType}
				visibleActions={{
					sort: canSortField,
					hide: canHideField,
					edit: editActionIsVisible,
				}}
			/>
		</ActionsWrapper>
	) : null;

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const calculateColumnIndex = useCallback(
		throttle((event: MouseEvent) => {
			if (!event.currentTarget) {
				return;
			}

			const rect = event.currentTarget.getBoundingClientRect();
			const x = event.clientX - rect.left;
			const width = rect.right - rect.left;

			if (x < width / 2 && x <= BOUNDARY) {
				setIndex(FIELD, index - 1);
			} else if (x >= width / 2 && x >= width - BOUNDARY) {
				setIndex(FIELD, index);
			} else {
				clearIndex(FIELD);
			}
		}, MOUSE_MOVE_THROTTLE),
		[index, setIndex, clearIndex],
	);

	const handleMouseEnter = useCallback(() => setIsHovering(true), []);

	const handleMouseLeave = useCallback(() => {
		setIsHovering(false);
		!isSelected && clearIndexAfterDelay(FIELD);
	}, [clearIndexAfterDelay, isSelected]);

	const handleMouseMove = useCallback(
		(event: MouseEvent) => {
			event.persist();

			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			if (ref.current?.contains(event.target as HTMLElement)) {
				calculateColumnIndex(event);
			} else {
				clearIndex(FIELD);
			}
		},
		[calculateColumnIndex, clearIndex],
	);

	const IconWrapperComponent = isVisualRefreshEnabled() ? VisualRefreshIconWrapper : IconWrapper;

	return (
		<ColumnHeaderContainer
			data-testid={COLUMN_HEADER_TEST_ID}
			ref={ref}
			dragStatus={dragStatus}
			isDragDisabled={isDragDisabled}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
			onMouseMove={handleMouseMove}
		>
			{isVisualRefreshEnabled() ? (
				<Flex alignItems="center" xcss={[titleStyle, index > 0 && columnStyle]}>
					<Box xcss={textWrapperStyle}>{customLabel}</Box>
				</Flex>
			) : (
				<Title>
					{isHovering && isDragDisabled !== true ? (
						<ColumnDragHandle data-testid={COLUMN_HEADER_DRAG_HANDLE_TEST_ID} />
					) : (
						icon != null && <IconWrapperComponent>{icon}</IconWrapperComponent>
					)}
					<TextWrapper>{customLabel}</TextWrapper>
				</Title>
			)}
			{dragStatus === 'idle' ? columnActions : null}
			{isDragDisabled !== true ? dropTargetJSX : null}
		</ColumnHeaderContainer>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ColumnDragHandle = styled.div({
	'&::before': {
		content: "'⠿'",
		color: token('color.text.subtle'),
		width: '24px',
		display: 'flex',
		justifyContent: 'center',
		visibility: 'hidden',
		marginRight: token('space.050'),
		marginLeft: token('space.050'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'.BaseTable__header-cell:hover &': {
		'&::before': {
			visibility: 'visible',
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	[`.${COLUMN_DRAGGABLE_HELPER_CLASS} &`]: {
		'&::before': {
			visibility: 'visible',
		},
	},
});

const iconWrapperStyle = xcss({
	height: 'size.200',
	width: 'size.200',
	marginRight: 'space.050',
	marginLeft: 'space.050',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const ActionsWrapperOld = styled.div({
	position: 'relative',
	flexGrow: 1,
	height: '100%',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	marginTop: 0,
	marginRight: token('space.050'),
	marginBottom: 0,
	marginLeft: token('space.050'),

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'.BaseTable__header-cell:last-child:hover &': {
		flexGrow: 0,
	},
});

const actionsWrapperStyles = css({
	position: 'relative',
	flexGrow: 1,
	height: '100%',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'flex-end',
	gap: token('space.050'),
	marginTop: 0,
	marginRight: token('space.050'),
	marginBottom: 0,
	marginLeft: token('space.050'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'.BaseTable__header-cell:last-child:hover &': {
		flexGrow: 0,
	},
});

const ActionsWrapper = componentWithCondition(
	() => isVisualRefreshEnabled(),
	({ children }: PropsWithChildren<{}>) => (
		<div
			data-testid="business-list.ui.list-view.column-header.actions-wrapper"
			css={actionsWrapperStyles}
		>
			{children}
		</div>
	),
	ActionsWrapperOld,
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const IconWrapper = styled.div({
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${gridSize * 3}px`,
	flexGrow: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${gridSize * 3}px`,
	marginRight: token('space.050'),
	marginLeft: token('space.050'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const TextWrapper = styled.div({
	flexGrow: 0,
	whiteSpace: 'nowrap',
	textOverflow: 'ellipsis',
	overflow: 'hidden',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const Title = styled.div({
	display: 'flex',
	alignItems: 'center',
	flexGrow: 0,
	overflow: 'hidden',
	height: '100%',
});

const titleStyle = xcss({
	flexGrow: 0,
	overflow: 'hidden',
	height: '100%',
	font: 'font.heading.xxsmall',
	color: 'color.text.subtle',
});

const columnStyle = xcss({
	paddingLeft: 'space.100',
});

const textWrapperStyle = xcss({
	flexGrow: 0,
	whiteSpace: 'nowrap',
	textOverflow: 'ellipsis',
	overflow: 'hidden',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const ColumnHeaderContainer = styled.div<{
	isDragDisabled?: boolean;
	dragStatus?: DraggableStatus;
}>({
	display: 'flex',
	alignItems: 'center',
	width: '100%',
	height: '100%',
	position: 'relative',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	...({ isDragDisabled }) => (isDragDisabled === false ? { cursor: 'grab' } : {}),

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	...({ dragStatus }) =>
		dragStatus === 'dragging'
			? {
					background: token('color.background.disabled'),
					color: token('color.text.disabled'),
					cursor: 'grabbing',
				}
			: {},
});
